import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase.js';
import { Users, Appointments, VinChecker, Settings, Outreach, PromoCodeGenerator, Drivers, RarCounter, Invoices, Clients, LiveLogsTab, AutoShop, Notifications, Parking, Marketing, Email, Whatsapp } from './index.js';
import { Sidebar, TopBar } from '../layout/index.js';
import { DashboardContent } from '../dashboard/index.js';
import { getApiClient } from '../../api/apiClient';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { 
  ChartBarIcon, 
  UsersIcon, 
  CalendarDaysIcon, 
  MagnifyingGlassIcon,
  CogIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  MapPinIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  ChatBubbleOvalLeftEllipsisIcon
} from '@heroicons/react/24/outline';

const Dashboard = ({ user }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    appointments: [],
    vinChecks: []
  });

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Fetch data for dashboard stats
  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);
      const client = getApiClient();
      const [appointmentsResponse, vinChecksResponse] = await Promise.all([
        client.get('/admin/onlineAppoints'),
        client.get('/admin/vinChecks')
      ]);

      setDashboardData({
        appointments: appointmentsResponse.data || [],
        vinChecks: vinChecksResponse.data || []
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData({
        appointments: [],
        vinChecks: []
      });
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Map URL paths to view names
  const pathToView = {
    '/': 'dashboard',
    '/dashboard': 'dashboard',
    '/clients': 'clients',
    '/users': 'users',
    '/autoshop': 'autoshop',
    '/drivers': 'drivers',
    '/parking': 'parking',
    '/appointments': 'appointments',
    '/vin-checker': 'vin-checker',
    '/rar-counter': 'rar-counter',
    '/invoices': 'invoices',
    '/outreach': 'outreach',
    '/promo-code': 'promo-code',
    '/live-logs': 'live-logs',
    '/notifications': 'notifications',
    '/marketing': 'marketing',
    '/email': 'email',
    '/whatsapp': 'whatsapp',
    '/settings': 'settings'
  };

  const viewToPath = {
    'dashboard': '/dashboard',
    'clients': '/clients',
    'users': '/users',
    'drivers': '/drivers',
    'autoshop': '/autoshop',
    'parking': '/parking',
    'appointments': '/appointments',
    'vin-checker': '/vin-checker',
    'rar-counter': '/rar-counter',
    'invoices': '/invoices',
    'outreach': '/outreach',
    'promo-code': '/promo-code',
    'live-logs': '/live-logs',
    'notifications': '/notifications',
    'marketing': '/marketing',
    'email': '/email',
    'whatsapp': '/whatsapp',
    'settings': '/settings'
  };

  const whatsappDeepLinkPhoneNumber = location.pathname.startsWith('/whatsapp/')
    ? decodeURIComponent(location.pathname.slice('/whatsapp/'.length))
    : '';

  // Sync activeView with URL on location change
  useEffect(() => {
    const currentView = location.pathname.startsWith('/whatsapp/')
      ? 'whatsapp'
      : (pathToView[location.pathname] || 'dashboard');
    setActiveView(currentView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Update URL when activeView changes
  const handleViewChange = (view) => {
    const path = viewToPath[view] || '/dashboard';
    navigate(path);
    setActiveView(view);
  };

  // Calculate dynamic stats
  const calculateStats = () => {
    const { appointments, vinChecks } = dashboardData;
    
    const totalAppointments = appointments.length;
    const confirmedAppointments = appointments.filter(apt => apt.confirmed).length;
    const totalVinChecks = vinChecks.length;
    const paidVinChecks = vinChecks.filter(check => check.paid).length;
    const vinChecksWithInfo = vinChecks.filter(check => check.vehicleInfo).length;
    
    // Extract emails from nested appointment data structure
    const appointmentEmails = appointments.map(apt => apt.appointmentData?.email).filter(Boolean);
    const vinCheckEmails = vinChecks.map(check => check.email).filter(Boolean);
    
    const uniqueUsers = new Set([
      ...appointmentEmails,
      ...vinCheckEmails
    ]).size;

    return [
      { 
        name: t('totalAppointments'), 
        value: totalAppointments.toString(), 
        change: confirmedAppointments > 0 ? `${confirmedAppointments} ${t('confirmed')}` : t('noConfirmedAppointments'),
        changeType: confirmedAppointments > 0 ? 'positive' : 'neutral'
      },
      { 
        name: t('vinChecks'), 
        value: totalVinChecks.toString(), 
        change: `${paidVinChecks} ${t('paid')}, ${vinChecksWithInfo} ${t('vehicleInfo')}`,
        changeType: paidVinChecks > 0 ? 'positive' : 'neutral'
      },
      { 
        name: t('uniqueUsers'), 
        value: uniqueUsers.toString(), 
        change: t('acrossAllServices'),
        changeType: 'neutral'
      },
      { 
        name: t('paymentRate'), 
        value: totalVinChecks > 0 ? `${Math.round((paidVinChecks / totalVinChecks) * 100)}%` : '0%',
        change: `${paidVinChecks} ${t('ofVinChecks')} ${totalVinChecks}`,
        changeType: paidVinChecks > 0 ? 'positive' : 'negative'
      },
    ];
  };

  const stats = calculateStats();

  const navigation = [
    { name: t('dashboard'), view: 'dashboard', current: activeView === 'dashboard', icon: ChartBarIcon },
    { name: t('clients'), view: 'clients', current: activeView === 'clients', icon: UsersIcon },
    { name: t('autoShop'), view: 'autoshop', current: activeView === 'autoshop', icon: UsersIcon },
    { name: t('drivers'), view: 'drivers', current: activeView === 'drivers', icon: UsersIcon },
    { name: t('parking'), view: 'parking', current: activeView === 'parking', icon: MapPinIcon },
    { name: t('appointments'), view: 'appointments', current: activeView === 'appointments', icon: CalendarDaysIcon },
    { name: t('vinChecker'), view: 'vin-checker', current: activeView === 'vin-checker', icon: MagnifyingGlassIcon },
    { name: t('rarCounter'), view: 'rar-counter', current: activeView === 'rar-counter', icon: DocumentDuplicateIcon },
    { name: t('invoices'), view: 'invoices', current: activeView === 'invoices', icon: DocumentTextIcon },
    // { name: t('outreach'), view: 'outreach', current: activeView === 'outreach', icon: ChatBubbleLeftRightIcon },
    // { name: t('promoGenerator'), view: 'promo-code', current: activeView === 'promo-code', icon: CogIcon },
    { name: t('marketing') || 'Marketing', view: 'marketing', current: activeView === 'marketing', icon: MegaphoneIcon },
    { name: t('email') || 'Email', view: 'email', current: activeView === 'email', icon: EnvelopeIcon },
    { name: 'WhatsApp', view: 'whatsapp', current: activeView === 'whatsapp', icon: ChatBubbleOvalLeftEllipsisIcon },
    { name: t('notifications'), view: 'notifications', current: activeView === 'notifications', icon: ChatBubbleLeftRightIcon },
    { name: t('liveLogs'), view: 'live-logs', current: activeView === 'live-logs', icon: ChartBarIcon },
    { name: t('settings'), view: 'settings', current: activeView === 'settings', icon: CogIcon },
  ];

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      <Sidebar 
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        navigation={navigation}
        activeView={activeView}
        setActiveView={handleViewChange}
        user={user}
        handleLogout={handleLogout}
        t={t}
      />

      {/* Main content */}
      <div className="md:pl-64 flex flex-col h-full">
        <TopBar 
          setSidebarOpen={setSidebarOpen}
          navigation={navigation}
          activeView={activeView}
          user={user}
          handleLogout={handleLogout}
          t={t}
        />

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          {activeView === 'dashboard' && <DashboardContent stats={stats} dashboardData={dashboardData} loading={dashboardLoading} />}
          {activeView === 'clients' && <Clients />}
          {activeView === 'users' && <Users />}
          {activeView === 'autoshop' && <AutoShop />}
          {activeView === 'parking' && <Parking />}
          {activeView === 'appointments' && <Appointments appointments={dashboardData.appointments} />}
          {activeView === 'vin-checker' && <VinChecker vinChecks={dashboardData.vinChecks} />}
          {activeView === 'rar-counter' && <RarCounter />}
          {activeView === 'invoices' && <Invoices />}
          {activeView === 'outreach' && <Outreach />}
          {activeView === 'promo-code' && <PromoCodeGenerator />}
          {activeView === 'drivers' && <Drivers />}
          {activeView === 'settings' && <Settings />}
          {activeView === 'live-logs' && <LiveLogsTab />}
          {activeView === 'notifications' && <Notifications />}
          {activeView === 'marketing' && <Marketing />}
          {activeView === 'email' && <Email />}
          {activeView === 'whatsapp' && <Whatsapp deepLinkPhoneNumber={whatsappDeepLinkPhoneNumber} />}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;